@use '@/styles/tokens' as *;

// Ticket Preview — Figma 430:396527 (Approved) / 430:415084 (Pending) / 430:398072 (Rejected),
// panel 430:411829, header 430:411899, identity 944:562928, thumbnails 430:411839,
// reason card 430:411852, timeline 430:411860, rejection callout 430:411894 / 430:413520.
// Panel chrome (cream header band, BG Pattern, 24px body padding, CloseButton, width 464) belongs
// to <Drawer> and is not restyled here — see plan.md § Design spec § Accepted snaps and raw px.

// Header cluster, rendered inside <Drawer title> (OverlayChrome's <h2>). Figma sets 14px between
// the title group and the chip and 10px between badge and title; neither has a token, so one flat
// 12px is used — the value the shipped admin twin ships.
.header {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
}

.iconBadge {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 32px; // Figma 430:411902 — fixed 32×32 badge, no size token exists
  height: 32px;
  border-radius: $radius-pill;
  background: $color-accent1-900; // Figma Accent 1/accent-900 #656B25 — exact
  color: var(--text-inverse);
}

.headerHeading {
  font-size: $font-size-18;
  font-weight: $font-weight-medium;
  // Overrides OverlayChrome's Funnel Display 24px title. Figma's raw #101828 snapped to the ramp.
  font-family: $font-family-base;
  letter-spacing: normal;
  color: var(--text-primary);
}

// The four body sections sit 22px apart in Figma 430:396527 and 430:398072. No step in the space
// scale is 22 (20 and 24 straddle it), and one literal beats four wrong tokens.
.body {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.identity {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.name {
  font-size: $font-size-16;
  font-weight: $font-weight-medium;
  color: var(--text-primary);
  overflow-wrap: anywhere;
}

.subtitle {
  font-size: $font-size-13;
  font-weight: $font-weight-regular;
  color: var(--text-muted);
  overflow-wrap: anywhere;
}

// Figma 430:411834 — 10px between the identity block and the thumbnails.
.identityGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

// A fixed 4-column grid, NOT flex-wrap: the AC requires four per row, and the drawer body is
// 416px (464 - 2×24) while Figma's column is 424 — so 4×92 + 3×17.25 = 419.75 overflows and a flex
// row would wrap the fourth tile into a second row. Four explicit columns hold at any body width;
// under a scrollbar the tiles shrink instead of wrapping.
.thumbGrid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-4); // Figma 17.25px — 16 is the nearest step
  margin: 0;
  padding: 0;
  list-style: none;
}

.thumb {
  aspect-ratio: 1; // Figma 92×92; the grid cell owns the width
  overflow: hidden;
  background: $color-neutral-100; // Figma #F6F8F7 — nearest on the ramp
}

// The tile is the whole button (ticket 04). A full reset so the `<button>` is invisible: the
// look is `.thumb`'s and the image's, unchanged from ticket 03.
.thumbButton {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

.thumbImg {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

// Reason + description (Figma 430:411852): white card, subtle border, 10px radius.
.reasonCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: 0;
  padding: 13px 17px; // Figma's own; neither value has a --space step
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: 10px; // between $radius-lg (8) and $radius-xl (12)
}

.reasonHeading {
  margin: 0;
  font-size: $font-size-15;
  font-weight: $font-weight-medium;
  color: var(--text-tertiary);
}

.description {
  margin: 0;
  font-size: $font-size-14;
  line-height: 1.5;
  color: var(--text-tertiary);
  overflow-wrap: break-word; // AC: the complete description, wrapped rather than truncated
}

// Figma 430:411862: Geist 14/R, raw #6A7282 with no Figma variable — --text-muted (#777777) is the
// nearest token and the value the shipped twin ships.
.timelineHeading {
  margin: 0 0 var(--space-6); // Figma 24px, heading → first entry
  font-size: $font-size-14;
  font-weight: $font-weight-regular;
  color: var(--text-muted);
}

// The dot (8px, --border-default #CCCCCC) and the 11px --text-disabled timestamp are already exact
// in <Timeline>. Only the connector differs: Figma's Line 174 is a 1px DASHED stroke in
// LightGreen/500 (`stroke-dasharray="2 2"`), where antd ships a solid near-black 9% rail.
//
// `-rail`, NOT `-tail`. antd 6 builds `<Timeline>` on top of `<Steps>`, so the connector element is
// `.ant-steps-item-rail` / `.ant-timeline-item-rail`; `.ant-timeline-item-tail` is antd 5's name and
// matches nothing here. Verified in the running app — the shared component's own rule still targets
// the old name and is dead, which is why the default rail colour reached the screen.
// The doubled class is not a typo. antd sets the rail's width from a vertical-steps rule of its
// own, which outranks a plain descendant match; repeating the class buys one point of specificity
// without inventing a structural selector that antd is free to re-nest (it already moved this
// element once, from `-tail` in v5 to `-rail` inside `-header` in v6).
.timeline :global(.ant-timeline-item-rail.ant-timeline-item-rail) {
  border-inline-start-style: dashed;
  // Figma's stroke is 1px; antd's rail defaults to 2. A hairline, like the reason card's border —
  // the scale has no sub-4px step.
  border-inline-start-width: 1px;
  border-inline-start-color: $color-light-green-500;
}

// Rejected only, below the timeline (Figma 430:413520).
.rejection {
  display: flex;
  flex-direction: column;
  gap: 6px; // Figma's own; below the 8px step
  margin: 0;
  padding: var(--space-3);
  background: $color-error-50; // Figma #FFF6F6
  border-radius: $radius-xl; // Figma 12px
}

.rejectionHeading {
  display: inline-flex;
  align-items: center;
  gap: 6px; // Figma 430:411895 — same sub-token step as the box above
  font-size: $font-size-14;
  font-weight: $font-weight-regular;
  color: $color-stage-attention-text; // Figma #A2191F — the X glyph inherits this
}

.rejectionValue {
  margin: 0;
  font-size: $font-size-12;
  color: $color-error-950; // Figma #560D10
  overflow-wrap: break-word;
}

// No Figma frame covers loading (context.md discrepancy #24), so the blocks reuse the shimmer the
// list panel already ships, sized to the real sections so nothing reflows when data lands.
.skeleton {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.skeletonBlock {
  border-radius: $radius-lg;
  // `@mixin shimmer` deliberately ships no base colour — each consumer sets its own, as the claim
  // rows do. Same surface as the list panel's rows, so the two panels shimmer identically.
  background-color: var(--surface-muted);
  @include shimmer;
}

.skeletonIdentity {
  height: 42px;
}

.skeletonThumbs {
  height: 92px;
}

.skeletonCard {
  height: 156px;
}

.skeletonTimeline {
  height: 160px;
}

// The load-failure well, matching the listing panel's.
.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  height: 100%;
  // Centring floor, so the state still reads as centred when the panel body is short. Not a Figma
  // value and no token exists; character-for-character the listing panel's.
  min-height: 240px;
}

.stateText {
  margin: 0;
  font-size: $font-size-14;
  color: var(--text-muted);
  text-align: center;
}
